import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Palette,
  Image,
  Sun,
  Moon,
  Save,
  Loader2,
  RefreshCw,
  Check,
  Upload,
  X
} from 'lucide-react';
import MediaPicker from './MediaPicker';
import { toRelativeMediaPath } from '../utils/mediaUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminThemeSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('logos');
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaPickerTarget, setMediaPickerTarget] = useState(null); // 'dark_mode' or 'light_mode'
  
  const { toast } = useToast();
  const { token } = useAuth();
  
  const headers = { Authorization: `Bearer ${token}` };
  
  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/theme-settings`, { headers });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching theme settings:', error);
      toast({ title: 'Feil', description: 'Kunne ikke hente tema-innstillinger', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [token, toast]);
  
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/theme-settings`, settings, { headers });
      toast({ title: 'Lagret', description: 'Tema-innstillinger er oppdatert' });
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving theme settings:', error);
      toast({ title: 'Feil', description: 'Kunne ikke lagre innstillinger', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };
  
  const handleThemeModeChange = async (mode) => {
    try {
      const response = await axios.put(`${API}/theme-settings/mode/${mode}`, {}, { headers });
      setSettings(response.data);
      toast({ title: 'Tema endret', description: `Tema er satt til ${mode === 'dark' ? 'mørkt' : 'lyst'}` });
    } catch (error) {
      console.error('Error changing theme mode:', error);
      toast({ title: 'Feil', description: 'Kunne ikke endre tema', variant: 'destructive' });
    }
  };
  
  const handleLogoChange = (logoType, url) => {
    setSettings(prev => ({
      ...prev,
      logos: {
        ...prev.logos,
        [logoType]: url
      }
    }));
    setHasChanges(true);
  };
  
  const openMediaPicker = (target) => {
    setMediaPickerTarget(target);
    setShowMediaPicker(true);
  };
  
  const handleMediaSelect = (media) => {
    if (mediaPickerTarget) {
      const imageUrl = toRelativeMediaPath(media.url || `/api/media/file/${media.filename}`);
      handleLogoChange(mediaPickerTarget, imageUrl);
      toast({ 
        title: 'Logo valgt', 
        description: `${mediaPickerTarget === 'dark_mode' ? 'Mørk modus' : 'Lys modus'} logo er oppdatert`
      });
    }
    setShowMediaPicker(false);
    setMediaPickerTarget(null);
  };
  
  const getLogoUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
        <span className="ml-3 text-gray-400">Laster tema-innstillinger...</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="admin-theme-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Palette className="w-7 h-7 text-yellow-500" />
            Tema & Utseende
          </h1>
          <p className="text-gray-400 mt-1">Administrer logoer, tema og farger</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={fetchSettings}
            variant="outline"
            className="border-gray-600"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Oppdater
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-yellow-600 hover:bg-yellow-700"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Lagre endringer
          </Button>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-800">
          <TabsTrigger value="logos" className="data-[state=active]:bg-yellow-600">
            <Image className="w-4 h-4 mr-2" />
            Logoer
          </TabsTrigger>
          <TabsTrigger value="theme" className="data-[state=active]:bg-yellow-600">
            <Palette className="w-4 h-4 mr-2" />
            Tema
          </TabsTrigger>
        </TabsList>
        
        {/* Logos Tab */}
        <TabsContent value="logos" className="space-y-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Image className="w-5 h-5 text-blue-400" />
                Logo-innstillinger
              </CardTitle>
              <CardDescription className="text-gray-400">
                Velg logoer for lys og mørk bakgrunn. Anbefalt format: PNG med transparent bakgrunn.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Dark Mode Logo */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-gray-300 text-lg flex items-center gap-2">
                      <Moon className="w-4 h-4" />
                      Logo for mørk bakgrunn
                    </Label>
                    <p className="text-gray-500 text-sm mt-1">
                      Brukes når nettsiden vises i mørkt tema (nåværende standard)
                    </p>
                  </div>
                  <Button
                    onClick={() => openMediaPicker('dark_mode')}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Velg fra mediearkiv
                  </Button>
                </div>
                
                <div className="flex items-center gap-4">
                  <Input
                    value={settings?.logos?.dark_mode || ''}
                    onChange={(e) => handleLogoChange('dark_mode', e.target.value)}
                    placeholder="/images/logo-light.png eller https://..."
                    className="bg-gray-900 border-gray-600 text-white flex-1"
                  />
                  {settings?.logos?.dark_mode && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleLogoChange('dark_mode', '')}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                
                {/* Preview */}
                <div className="p-6 bg-gray-900 rounded-lg border border-gray-700">
                  <p className="text-gray-500 text-xs mb-3">Forhåndsvisning (mørk bakgrunn):</p>
                  {settings?.logos?.dark_mode ? (
                    <img 
                      src={getLogoUrl(settings.logos.dark_mode)}
                      alt="Logo for mørk bakgrunn"
                      className="h-16 w-auto object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="h-16 flex items-center text-gray-500">
                      Ingen logo valgt
                    </div>
                  )}
                </div>
              </div>
              
              <div className="border-t border-gray-700" />
              
              {/* Light Mode Logo */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-gray-300 text-lg flex items-center gap-2">
                      <Sun className="w-4 h-4" />
                      Logo for lys bakgrunn
                    </Label>
                    <p className="text-gray-500 text-sm mt-1">
                      Brukes når nettsiden vises i lyst tema
                    </p>
                  </div>
                  <Button
                    onClick={() => openMediaPicker('light_mode')}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Velg fra mediearkiv
                  </Button>
                </div>
                
                <div className="flex items-center gap-4">
                  <Input
                    value={settings?.logos?.light_mode || ''}
                    onChange={(e) => handleLogoChange('light_mode', e.target.value)}
                    placeholder="/images/logo-dark.png eller https://..."
                    className="bg-gray-900 border-gray-600 text-white flex-1"
                  />
                  {settings?.logos?.light_mode && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleLogoChange('light_mode', '')}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                
                {/* Preview */}
                <div className="p-6 bg-white rounded-lg border border-gray-300">
                  <p className="text-gray-500 text-xs mb-3">Forhåndsvisning (lys bakgrunn):</p>
                  {settings?.logos?.light_mode ? (
                    <img 
                      src={getLogoUrl(settings.logos.light_mode)}
                      alt="Logo for lys bakgrunn"
                      className="h-16 w-auto object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="h-16 flex items-center text-gray-400">
                      Ingen logo valgt
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Theme Tab */}
        <TabsContent value="theme" className="space-y-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Palette className="w-5 h-5 text-purple-400" />
                Tema-modus
              </CardTitle>
              <CardDescription className="text-gray-400">
                Velg om nettsiden skal vises i mørkt eller lyst tema
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleThemeModeChange('dark')}
                  className={`p-6 rounded-xl border-2 transition-all ${
                    settings?.theme_mode === 'dark'
                      ? 'border-yellow-500 bg-gray-900'
                      : 'border-gray-600 bg-gray-900/50 hover:border-gray-500'
                  }`}
                >
                  <div className="flex flex-col items-center gap-3">
                    <Moon className={`w-12 h-12 ${settings?.theme_mode === 'dark' ? 'text-yellow-500' : 'text-gray-400'}`} />
                    <div className="text-center">
                      <p className="text-white font-semibold">Mørkt tema</p>
                      <p className="text-gray-500 text-sm">Mørk bakgrunn med lys tekst</p>
                    </div>
                    {settings?.theme_mode === 'dark' && (
                      <div className="flex items-center gap-1 text-green-400 text-sm">
                        <Check className="w-4 h-4" />
                        Aktiv
                      </div>
                    )}
                  </div>
                </button>
                
                <button
                  onClick={() => handleThemeModeChange('light')}
                  className={`p-6 rounded-xl border-2 transition-all ${
                    settings?.theme_mode === 'light'
                      ? 'border-yellow-500 bg-white'
                      : 'border-gray-600 bg-gray-100/10 hover:border-gray-500'
                  }`}
                >
                  <div className="flex flex-col items-center gap-3">
                    <Sun className={`w-12 h-12 ${settings?.theme_mode === 'light' ? 'text-yellow-600' : 'text-gray-400'}`} />
                    <div className="text-center">
                      <p className={settings?.theme_mode === 'light' ? 'text-gray-900 font-semibold' : 'text-white font-semibold'}>Lyst tema</p>
                      <p className={settings?.theme_mode === 'light' ? 'text-gray-600 text-sm' : 'text-gray-500 text-sm'}>Lys bakgrunn med mørk tekst</p>
                    </div>
                    {settings?.theme_mode === 'light' && (
                      <div className="flex items-center gap-1 text-green-600 text-sm">
                        <Check className="w-4 h-4" />
                        Aktiv
                      </div>
                    )}
                  </div>
                </button>
              </div>
              
              <p className="text-gray-500 text-sm mt-4 text-center">
                Merk: Lyst tema er under utvikling. Mørkt tema anbefales foreløpig.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Media Picker Modal */}
      {showMediaPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-4xl w-full max-h-[80vh] overflow-auto">
            <div className="sticky top-0 bg-gray-800 p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                Velg logo fra mediebibliotek
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowMediaPicker(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4">
              <MediaPicker 
                onSelect={handleMediaSelect}
                onClose={() => setShowMediaPicker(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminThemeSettings;
